/**
 * User avatar menu shown in the global header.
 * Displays user initials and provides links to Settings and Sign Out.
 */

import { useState } from 'react';
import { IconButton, Avatar, ListItemIcon, Menu, MenuItem, Box, Typography, Divider } from '@material-ui/core';
import SettingsIcon from '@material-ui/icons/Settings';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';

export const UserMenu = ({ displayName }: { displayName: string }) => {
  const identityApi = useApi(identityApiRef);
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const initials = displayName
    .split('.')
    .map(p => p[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 2) || 'G';

  return (
    <>
      <IconButton
        aria-label="Open user menu"
        aria-controls={anchor ? 'user-menu' : undefined}
        aria-haspopup="menu"
        onClick={e => setAnchor(e.currentTarget)}
        style={{
          padding: 3,
          border: '2px solid rgba(255,255,255,0.2)',
          borderRadius: '50%',
          transition: 'border-color 0.2s',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.5)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.2)'; }}
      >
        <Avatar style={{
          width: 32,
          height: 32,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: 0.5,
        }}>
          {initials}
        </Avatar>
      </IconButton>
      <Menu
        id="user-menu"
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          style: {
            borderRadius: 14,
            marginTop: 8,
            minWidth: 200,
            boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.08)',
          },
        }}
      >
        {/* User info section */}
        <Box style={{ padding: '14px 18px 10px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar style={{
            width: 36,
            height: 36,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: 'white',
            fontSize: 14,
            fontWeight: 700,
          }}>
            {initials}
          </Avatar>
          <Box>
            <Typography style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
              {displayName}
            </Typography>
          </Box>
        </Box>
        <Divider style={{ margin: '6px 0' }} />
        <MenuItem onClick={() => { setAnchor(null); navigate('/settings'); }} style={{ padding: '10px 18px', borderRadius: 8, margin: '2px 6px' }}>
          <ListItemIcon style={{ minWidth: 32 }}><SettingsIcon fontSize="small" /></ListItemIcon>
          <Typography style={{ fontSize: 14 }}>Settings</Typography>
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); identityApi.signOut(); }} style={{ padding: '10px 18px', borderRadius: 8, margin: '2px 6px' }}>
          <ListItemIcon style={{ minWidth: 32 }}><ExitToAppIcon fontSize="small" /></ListItemIcon>
          <Typography style={{ fontSize: 14 }}>Sign Out</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};
